import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  pri: '#0a0e27',
  pri2: '#2952CC',
  accent: '#6382eb',
  accent2: '#4f6ef2',
  bg: '#f7f8fc',
  white: '#FFFFFF',
  text: '#444',
  muted: '#666',
  border: '#eef0f5',
  green: '#10b981',
  purple: '#8b5cf6',
  amber: '#f59e0b',
  pink: '#ec4899',
  teal: '#14b8a6',
};

const S = {
  wrapper: {
    minHeight: '100vh',
    fontFamily: "'Inter','Segoe UI',system-ui,-apple-system,sans-serif",
    background: C.bg,
  },
  header: {
    background: `linear-gradient(135deg,${C.pri} 0%,#111c4e 50%,#1a2f7a 100%)`,
    padding: 'clamp(32px,5vw,48px) clamp(16px,4vw,20px) clamp(48px,8vw,64px)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: 'rgba(255,255,255,0.70)',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    position: 'relative',
    zIndex: 1,
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: '8px 4px',
    minHeight: '44px',
    minWidth: '44px',
  },
  h1: {
    fontSize: 'clamp(24px,4vw,44px)',
    fontWeight: 800,
    color: C.white,
    margin: '0 0 12px',
    position: 'relative',
    zIndex: 1,
  },
  sub: {
    fontSize: 'clamp(14px,1.8vw,16px)',
    color: 'rgba(255,255,255,0.55)',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: 1.7,
    position: 'relative',
    zIndex: 1,
    padding: '0 16px',
  },
  body: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: 'clamp(32px,5vw,48px) clamp(16px,4vw,20px) 80px',
  },
  sectionTitle: {
    fontSize: 'clamp(18px,2.5vw,22px)',
    fontWeight: 700,
    color: C.pri,
    margin: '0 0 8px',
    paddingBottom: '12px',
    borderBottom: `2px solid ${C.accent}`,
  },
  sectionSub: {
    fontSize: 'clamp(14px,1.6vw,15px)',
    color: C.muted,
    lineHeight: 1.7,
    marginBottom: '24px',
  },
  roadmapSection: { marginBottom: 'clamp(32px,5vw,48px)' },
  timelineItem: (open, color) => ({
    background: open ? `${color}0a` : C.white,
    borderRadius: 'clamp(12px,2vw,16px)',
    padding: 'clamp(16px,2vw,24px)',
    border: open ? `2px solid ${color}` : `1px solid ${C.border}`,
    boxShadow: open ? `0 4px 20px ${color}20` : '0 1px 3px rgba(0,0,0,0.04)',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  timelineHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  timelineDot: (color) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),
  timelineTitle: {
    fontSize: 'clamp(15px,1.7vw,17px)',
    fontWeight: 700,
    color: C.pri,
    flex: 1,
    minWidth: '200px',
  },
  timelineTag: (color) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '6px',
    background: `${color}15`,
    color: color,
    fontSize: '11px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  }),
  timelineContent: { paddingTop: '16px', overflow: 'hidden' },
  timelineText: { fontSize: 'clamp(13px,1.5vw,14px)', color: C.muted, lineHeight: 1.7 },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,280px), 1fr))',
    gap: 'clamp(12px,2vw,16px)',
    marginTop: '16px',
  },
  miniCard: {
    background: C.bg,
    borderRadius: '12px',
    padding: 'clamp(16px,2vw,20px)',
    border: `1px solid ${C.border}`,
  },
  miniH3: { fontSize: 'clamp(14px,1.6vw,15px)', fontWeight: 700, color: C.pri, margin: '0 0 6px' },
  miniP: { fontSize: 'clamp(13px,1.4vw,14px)', color: C.muted, lineHeight: 1.6 },
};

const SECCIONES = [
  {
    id: 'roles',
    titulo: 'Nuevos Roles Especializados',
    color: C.purple,
    desc: 'Roles adicionales para empresas con operaciones más complejas o rubros específicos.',
    items: [
      {
        t: 'Empresario',
        d: 'Rol por encima del admin. Controla múltiples empresas desde un solo panel. Ve reportes consolidados, todos los deliverys de todos sus negocios, métricas comparativas entre empresas.',
      },
      {
        t: 'Carnicero',
        d: 'Para negocios con carnicería. Recibe pedidos de cortes específicos, confirma pesos exactos, actualiza el inventario de productos pesables en tiempo real.',
      },
      {
        t: 'Depositario',
        d: 'Encargado de mantener el inventario al día. Reporta faltantes y sobrantes al supervisor. Responsable de la sincronización entre el stock físico y el sistema.',
      },
    ],
  },
  {
    id: 'features',
    titulo: 'Nuevas Funcionalidades',
    color: C.amber,
    desc: 'Mejoras planificadas para expandir las capacidades del sistema.',
    items: [
      {
        t: 'GPS en Vivo',
        d: 'Seguimiento en tiempo real de cada repartidor. El cliente ve la ubicación exacta de su pedido en el mapa en tiempo real, no solo una estimación.',
      },
      {
        t: 'Sistema de Voz y Audio',
        d: 'El cliente puede enviar notas de voz en lugar de escribir. El sistema transcribe automáticamente via Whisper (OpenAI). El bot responde normalmente. Accesibilidad mejorada.',
      },
      {
        t: 'Dashboard Avanzado',
        d: 'Pantallas de reportes con gráficos interactivos: pedidos por sede, tiempo promedio de entrega, productos más vendidos, eficiencia del equipo, tendencias por hora/día/mes.',
      },
    ],
  },
  {
    id: 'ia',
    titulo: 'Mejoras en la IA',
    color: C.teal,
    desc: 'El bot se vuelve mas inteligente con el tiempo gracias al aprendizaje continuo.',
    items: [
      {
        t: 'Autoaprendizaje',
        d: 'El bot registra ejemplos positivos y negativos de interacciones reales. Estos ejemplos se usan para refinar automáticamente el prompt y mejorar las respuestas futuras.',
      },
      {
        t: 'Métricas de Rendimiento',
        d: 'Panel con estadísticas del bot: tokens consumidos, costo en USD, cache hits vs misses, tiempo de respuesta promedio, conversaciones completadas vs abandonadas.',
      },
      {
        t: 'Entrenamiento por Empresa',
        d: 'Cada empresa entrena a su bot con su propio lenguaje, productos y técnicas de venta. El bot de una carnicería habla distinto al de una ferretería.',
      },
      {
        t: 'Detección de Intención Mejorada',
        d: 'El clasificador de intenciones se refina con los datos reales de cada empresa. Reduce falsos positivos y mejora la comprensión de mensajes ambiguos.',
      },
    ],
  },
];

function RoadmapDetalle() {
  const nav = useNavigate();
  const [abiertos, setAbiertos] = useState({});

  const toggle = (seccionId, itemIdx) => {
    const key = `${seccionId}-${itemIdx}`;
    setAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div style={S.wrapper}>
      <style>{'body{overflow:auto!important}'}</style>
      <div style={S.header}>
        <button onClick={() => nav('/#roadmap')} style={S.backBtn} aria-label="Volver al inicio">
          &#8592; Volver al inicio
        </button>
        <h1 style={S.h1}>Proximos Avances</h1>
        <p style={S.sub}>
          Agorax no es un producto estatico. Esta es una tecnologia en evolucion constante que crece
          con las necesidades de cada empresa.
        </p>
      </div>
      <div style={S.body}>
        <p style={{ ...S.sectionSub, textAlign: 'center', fontSize: '16px', marginBottom: '32px' }}>
          Haz clic en cada item para ver detalles de la funcionalidad planificada.
        </p>

        {SECCIONES.map((seccion) => (
          <div key={seccion.id} style={S.roadmapSection}>
            <h2 style={S.sectionTitle}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                <div style={S.timelineDot(seccion.color)} />
                {seccion.titulo}
              </span>
            </h2>
            <p style={S.sectionSub}>{seccion.desc}</p>
            {seccion.items.map((item, i) => {
              const key = `${seccion.id}-${i}`;
              const open = abiertos[key];
              return (
                <div
                  key={i}
                  style={S.timelineItem(open, seccion.color)}
                  onClick={() => toggle(seccion.id, i)}
                >
                  <div style={S.timelineHeader}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        flex: 1,
                        minWidth: '200px',
                      }}
                    >
                      <span style={S.timelineDot(seccion.color)} />
                      <span style={S.timelineTitle}>{item.t}</span>
                    </span>
                    <span
                      style={{
                        ...S.timelineTag(seccion.color),
                        display: open ? 'inline-block' : undefined,
                      }}
                    >
                      {open ? 'Cerrado' : 'Ver detalle'}
                    </span>
                  </div>
                  {open && (
                    <div style={S.timelineContent}>
                      <p style={S.timelineText}>{item.d}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <button
          onClick={() => nav('/#roadmap')}
          style={{
            ...S.backBtn,
            color: C.pri2,
            display: 'block',
            margin: '32px auto 0',
            fontSize: 'clamp(14px,1.6vw,16px)',
            fontWeight: 600,
          }}
          aria-label="Volver al inicio"
        >
          &#8592; Volver al inicio
        </button>
      </div>
    </div>
  );
}

export default RoadmapDetalle;
